import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert, Divider, Steps } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyCertificateOutlined, 
  CrownOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import '../AuthPage.css';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

/**
 * Admin onboarding authentication page
 */
const AdminAuthPage = () => {
  const { user, isAuthenticated, isLoading, error, login, register, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next') || '/admin';
      history.replace(nextUrl);
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      clearError();
      setTimeout(() => {
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login with admin role context
  const handleOIDCLogin = () => {
    clearError();
    login();
  };

  // Handle OIDC registration with admin role context
  const handleOIDCRegister = () => {
    clearError();
    register();
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main admin onboarding page
  return (
    <div className="auth-container">
      <Card className="auth-card onboarding-card">
        <div className="auth-content">
          {/* Header */}
          <div className="auth-header">
            <CrownOutlined className="auth-icon admin-icon" />
            <Title level={1} className="auth-title">
              Administrator Access
            </Title>
            <Text className="auth-subtitle">
              Manage your organization&apos;s educational platform with full control
            </Text>
          </div>

          {/* Onboarding Steps */}
          <div className="onboarding-steps">
            <Steps size="small" current={0}>
              <Step 
                title="Access" 
                icon={<UserOutlined />} 
                description="Verify your identity"
              />
              <Step 
                title="Configure" 
                icon={<SettingOutlined />} 
                description="Set up your system"
              />
              <Step 
                title="Manage" 
                icon={<DashboardOutlined />} 
                description="Control your platform"
              />
            </Steps>
          </div>

          {/* Benefits */}
          <div className="onboarding-benefits">
            <Title level={4}>Administrative capabilities:</Title>
            <div className="benefits-list">
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Full system configuration and management</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>User and role management</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Advanced analytics and reporting</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>System monitoring and maintenance</Text>
              </div>
            </div>
          </div>

          {/* Auth Actions */}
          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button oidc-button primary-cta"
              icon={<LockOutlined />}
              loading={isLoading}
            >
              Access Admin Dashboard
            </Button>
            
            <Divider>Need administrative access?</Divider>
            
            <Button 
              type="default" 
              size="large" 
              onClick={handleOIDCRegister}
              className="auth-button signup-button"
              icon={<UserOutlined />}
            >
              Request Admin Account
            </Button>
          </div>

          {/* Security Warning */}
          <div className="auth-info-section">
            <Alert
              message="Administrative Access"
              description="Admin accounts have elevated privileges. Ensure you are authorized to access this system."
              type="warning"
              showIcon
              className="admin-warning"
            />
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Enhanced security with multi-factor authentication
            </Paragraph>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <Text type="secondary">
              Unauthorized access is prohibited. <a href="mailto:security@strideahead.com">Report security issues</a>
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminAuthPage;